import { differenceInHours } from 'date-fns';
import type {
  ExposureStimulus,
  ExposureStimulusExposition,
} from '../../schema/be-graphql-generated';

/**
 * E21 · the exposure portal's PURE helpers — the era rules, the two affordance gates and the run
 * vocabulary, in ONE place because THREE screens ask them (`list.tsx`, `create.tsx`, `edit.tsx`) and
 * they used to answer differently. No I/O, no React: everything here is a question about a row.
 *
 * The one rule everything else follows from is T95a — **THE ROW DECIDES THE ERA**. A therapist's
 * roster is MIXED (plan 07 C3): some clients are `es`, some legacy, and even for an `es` client an
 * exposure the converter has not reached yet is still a legacy row whose writes take the legacy
 * path. So "may I offer the ES-only control?" is never `isEsClient` alone — it is `isEsClient AND
 * the row carries its ES identity`, and for a RUNG it is that AND the rung carries its own. Getting
 * this wrong renders a button the server refuses with `EXPOSURE_NOT_CONVERTED`.
 */

/** The `EsDomain` member the era gate asks for (plan 07 §2). */
export const EXPOSURE_DOMAIN = 'exposure';

/**
 * The codes this tool answers in its OWN copy rather than the server's.
 *
 * The two coexistence codes are here because the API localises its message by `Accept-Language`
 * but writes it for the ACCOUNT OWNER ("Your account was just updated"), and the portal's reader is
 * the therapist; every other coded rejection shows the server's copy verbatim (the E02 rule,
 * founder QA 2026-09-02).
 *
 * `REFUSED_NULL_ANSWER` is here for a different reason: the server said nothing at all, so there is
 * no message to show. It is minted client-side (see below) and its "message" is a diagnostic
 * string, never copy — routing it through this map is what keeps that string off the screen.
 */
export const COEXISTENCE_COPY_KEYS: Record<string, string> = {
  ES_MODE_MISMATCH: 'pages.exposure.esModeMismatch',
  ES_RESEND_AS_ES: 'pages.exposure.esResendAsEs',
  REFUSED_NULL_ANSWER: 'pages.exposure.writeRefused',
};

/** The bare legacy `_key` of an `exposures/<key>` id — for data-testids. */
export const rowKeyOf = (id: string | null | undefined): string =>
  (id ?? '').split('/').pop() ?? '';

// ── a `null` answer is a REFUSAL (review 2026-09-05, D6) ─────────────────────────────────────────

/**
 * The code an editor reports when the server answered `null` instead of a row.
 *
 * WHY THIS EXISTS. The shipped exposure resolvers answer a SILENT NULL for a permission miss and
 * for a ladder/client mismatch — that is the legacy contract, recorded on the board as
 * `{response: null}` and deliberately kept (founder G1 2026-08-28: the domain can see neither a
 * role nor a grant). Refine's `onSuccess` fires on `data: null` exactly as it does on a row, so
 * both editors toasted "Situation saved" while NOTHING had been written: a therapist whose grant
 * was revoked mid-session, or one who reached a tampered URL, was told their clinical note was
 * safe. Pre-existing since before event sourcing (the autosave did the same), and the one class of
 * failure this build may not keep: a save that did not happen must never read as one that did.
 *
 * It is a CODE, not copy, so `reportFailure` gives it the same three-tier treatment every other
 * refusal gets and Sentry sees it.
 */
export const REFUSED_NULL_ANSWER = 'REFUSED_NULL_ANSWER';

/**
 * Did the server answer with a row, or with nothing? `undefined` counts as nothing too — refine
 * hands back whatever the data provider parsed, and "no row" is the same statement either way.
 */
export const isRefusedAnswer = (answer: unknown): boolean =>
  answer === null || answer === undefined;

/**
 * The rejection a `null` answer becomes. It is shaped like a GraphQL error so `graphqlErrorInfo`
 * reads its code out of it without a special case — one error vocabulary, not two.
 */
export const refusedAnswerError = (operation: string): Error =>
  Object.assign(new Error(`${operation} answered null — nothing was written`), {
    errors: [
      {
        message: `${operation} answered null`,
        extensions: { code: REFUSED_NULL_ANSWER },
      },
    ],
  });

// ── era ──────────────────────────────────────────────────────────────────────────────────────────

type LadderRow = { exposureId?: string | null } | null | undefined;
type RungRow = { exposureStimulusId?: string | null } | null | undefined;

/** Is this exposure owned by the LOG? `Exposure.exposureId` is the identity the projection stamps. */
export const isLogOwnedExposure = (row: LadderRow): boolean =>
  Boolean(row?.exposureId);

/** Is this RUNG owned by the log? Both halves of the pair must be, before a run may be recorded. */
export const isLogOwnedStimulus = (row: RungRow): boolean =>
  Boolean(row?.exposureStimulusId);

export type EraVerdict = {
  /** The ES write path applies: an `es` client in a ready domain AND a log-owned row. */
  useEsPath: boolean;
  /** `es` client, but this row is still the converter's to reach — a transient, honest state. */
  unconvertedRow: boolean;
  /** What a `data-era` attribute should say — the machine-readable marker every spec reads. */
  era: 'es' | 'legacy';
};

/**
 * The era of ONE ladder, for a client whose mode is already known.
 *
 * `rowLoaded` matters: before the row arrives we cannot tell an unconverted exposure from one that
 * does not exist yet, and a NEW exposure (no row at all) legitimately takes the ES path — its first
 * save creates the boundary. Announcing "unconverted" while loading would flash a warning at every
 * therapist who opens the editor.
 *
 * ALL THREE ANSWERS COME FROM ONE PREDICATE (review 2026-09-05, D3). They used to come from two: a
 * LOCAL `useEsPath` that tolerated the loading window (`|| !rowLoaded`) fed `era`, while the
 * RETURNED `useEsPath` did not — a local silently shadowing the name it returns. For an `es` client
 * the page therefore said `data-era="es"` on first paint and flipped to `legacy` a round trip later
 * on an unconverted row: a page claiming an era it was not going to write in. The specs never saw
 * it because Playwright's assertions retry.
 *
 * The tie-break is deliberate and always the SAME direction: while the row is unknown the verdict
 * is the CAUTIOUS one. `useEsPath` gates ES-only controls, and offering one for a row the server
 * would refuse (`EXPOSURE_NOT_CONVERTED`, T95a) is the single thing this build may not do; the
 * cost is that a converted row announces itself one render late, which is a control appearing, not
 * a promise being withdrawn.
 */
export function exposureEra(opts: {
  isEsClient: boolean;
  row: LadderRow;
  rowLoaded: boolean;
}): EraVerdict {
  const { isEsClient, row, rowLoaded } = opts;
  const isNew = rowLoaded && !row;
  const logOwned = isLogOwnedExposure(row);
  const useEsPath = isEsClient && (isNew || logOwned);
  return {
    useEsPath,
    unconvertedRow: isEsClient && rowLoaded && !!row && !logOwned,
    era: useEsPath ? 'es' : 'legacy',
  };
}

/**
 * May the exposition RUNNER write through the ES commands for this rung? Both halves of the
 * parent–child pair have to be log-owned: the ladder is the DCB boundary and the rung is what the
 * run hangs off, so a converted ladder with an unconverted rung still routes legacy (the resolver
 * says so too — `resolveRung` throws `EXPOSURE_NOT_CONVERTED`).
 */
export const runnerUsesEs = (opts: {
  isEsClient: boolean;
  exposure: LadderRow;
  stimulus: RungRow;
}): boolean =>
  opts.isEsClient &&
  isLogOwnedExposure(opts.exposure) &&
  isLogOwnedStimulus(opts.stimulus);

// ── the two affordance gates, one implementation each ────────────────────────────────────────────

/** Everything the edit gate needs about a client — as the practice roster returns it. */
export type EditGateClient =
  | {
      accessLevel?: string | null;
      access?: {
        allowEdit?: boolean | null;
        allowAdminEdit?: boolean | null;
      } | null;
    }
  | null
  | undefined;

export type ExposureEditGate = {
  /** show the affordance at all — the therapist holds an edit relation to this client */
  offered: boolean;
  /** …and it can be pressed — the client's account is paid for */
  enabled: boolean;
  /** the tooltip that explains the state above */
  tooltipKey: string;
};

/**
 * May THIS therapist edit THIS client's exposure? The shipped L21 rule, unchanged — an edit relation
 * (`access.allowEdit`, or `allowAdminEdit` while the therapist is a THERAPIST_ADMIN) decides whether
 * the affordance is OFFERED, and the client's `accessLevel` (premium) decides whether it is USABLE.
 * A client without one gets the action DISABLED with the shipped explanation, never silently
 * removed.
 *
 * It lives here because three surfaces ask it and the shipped code answered it three times, inline,
 * with the same four-line expression — the shape that let E02's list and view disagree about who may
 * be edited (founder QA 2026-09-02, defect A).
 */
export function canEditExposure(
  client: EditGateClient,
  opts: { isTherapistAdmin?: boolean } = {},
): ExposureEditGate {
  const offered = Boolean(
    client?.access?.allowEdit ||
      (client?.access?.allowAdminEdit && opts.isTherapistAdmin),
  );
  const enabled = offered && Boolean(client?.accessLevel);
  return {
    offered,
    enabled,
    tooltipKey: enabled
      ? 'resourcesCommon.edit'
      : 'clients.thoughtsTestDisabledTip',
  };
}

/** The therapist's delete window: 2 hours from the rung's creation (L21, unchanged). */
export const DELETE_WINDOW_HOURS = 2;

/**
 * Is the 2-hour therapist delete window still open for this rung?
 *
 * The SERVER enforces this too, in both eras (`deleteExposureStimulus` returns null past the
 * window), and it must: a disabled button is an affordance, never a guard. This function exists so
 * the UI and the server cannot drift on the boundary — and so the rule is written down once instead
 * of being an inline `differenceInHours(...) > 2` in two render callbacks.
 *
 * A rung with NO `createdAt` is treated as inside the window, which is what the shipped code does
 * (`record?.createdAt ? … : false`) — every prod row has one, so this only covers a partial payload.
 */
export const deleteWindowOpen = (
  createdAt: string | Date | null | undefined,
  now: Date = new Date(),
): boolean => {
  if (!createdAt) return true;
  const at = createdAt instanceof Date ? createdAt : new Date(createdAt);
  if (Number.isNaN(at.getTime())) return true;
  return differenceInHours(now, at) <= DELETE_WINDOW_HOURS;
};

// ── the run vocabulary ───────────────────────────────────────────────────────────────────────────

export type Exposition = ExposureStimulusExposition;

/** The runs a rung holds, oldest first — an absent/`null` array reads as none (T102 (a)). */
export const runsOf = (
  stimulus: Pick<ExposureStimulus, 'expositions'> | null | undefined,
): Exposition[] =>
  (Array.isArray(stimulus?.expositions) ? stimulus!.expositions : []).filter(
    (r): r is Exposition => !!r,
  );

/**
 * The handle the ES commands address a run by. Present ⇔ `ExpositionStarted` created it; a run the
 * CONVERSION carried has none of its own and is addressed by its POSITION, whose derived handle the
 * projection reads back (`<exposureStimulusId>#<ordinal>` — T115 (a)). The portal never mints one:
 * `startExposition` answers with it.
 */
export const expositionHandle = (
  run: Exposition | null | undefined,
  exposureStimulusId: string,
  ordinal: number,
): string =>
  run?.expositionId ? run.expositionId : `${exposureStimulusId}#${ordinal}`;

/** One recorded SUD point of a run, as the row carries it. */
export type RunPoint = NonNullable<NonNullable<Exposition['suds']>[number]>;

/**
 * The handle a CORRECTION addresses a point by (founder ruling `amend-a-recorded-reaction`,
 * 2026-09-04). Present ⇔ `recordExpositionReaction` minted it; a point CONVERSION carried has none
 * of its own and is addressed by its POSITION, whose derived handle the projection reads back
 * (`<expositionId>#<ordinal>` — the same derivation `expositionHandle` makes one level up,
 * T115 (a)). The portal never mints one.
 *
 * It takes the RUN's handle, not the rung's: a carried run's own handle is already
 * `<exposureStimulusId>#<ordinal>`, so the point's is `<exposureStimulusId>#<run>#<point>` and the
 * server reads the ordinal off the last separator. Passing the rung here would build a handle the
 * fold can never match — the same defect Resume had on a carried run before WP6.
 */
export const reactionHandle = (
  point: RunPoint | null | undefined,
  expositionId: string,
  ordinal: number,
): string =>
  point?.reactionId ? String(point.reactionId) : `${expositionId}#${ordinal}`;

/** Has this point been corrected? Then `amendedFrom` holds the ORIGINAL reading. */
export const wasAmended = (point: RunPoint | null | undefined): boolean =>
  !!point?.amendedFrom &&
  typeof point.amendedFrom.sud === 'number' &&
  !!point.amendedFrom.reactedAt;

/**
 * The highest SUD of a run — the Exposition-history table's column.
 *
 * `Math.max()` of an empty list is `-Infinity`, and the shipped table renders it literally: a run
 * with no points shows "-Infinity" to the therapist. The E21 registry records it as a L21 quirk the
 * E-era build should guard, so it is guarded here, in the one place both the table and any later
 * chart can read.
 */
export const highestSud = (
  run: Exposition | null | undefined,
): number | null => {
  const points = (run?.suds ?? []).filter(
    (p): p is { sud: number; reactedAt: unknown } =>
      !!p && typeof p.sud === 'number',
  );
  return points.length ? Math.max(...points.map((p) => p.sud)) : null;
};

/**
 * How long a run has lasted, in seconds — the number `FinishExposition` records.
 *
 * THE RECORDED VALUE WINS, and the fallback is what the legacy table has always computed (the
 * span from the run's start to its last reaction), because the legacy record has no `duration`
 * key at all: the shipped app derived it on every render.
 *
 * It exists because RESUMING a run exposed a real defect in the live drive: the drawer's stopwatch
 * restarts at 0, so finishing a reopened run recorded `duration: 0` — a run that had visibly lasted
 * 46 seconds was written to the log as lasting none. Seeding the stopwatch from this function makes
 * a resumed run CONTINUE its clock, which is what "re-open a run, add points and stop again" means.
 */
export const runDurationSeconds = (
  run: Exposition | null | undefined,
): number => {
  if (typeof run?.duration === 'number' && run.duration >= 0) {
    return run.duration;
  }
  const points = (run?.suds ?? []).filter((p) => !!p?.reactedAt);
  const last = points[points.length - 1];
  if (!run?.createdAt || !last?.reactedAt) return 0;
  const started = new Date(String(run.createdAt)).getTime();
  const ended = new Date(String(last.reactedAt)).getTime();
  if (Number.isNaN(started) || Number.isNaN(ended)) return 0;
  return Math.max(0, Math.round((ended - started) / 1000));
};

/** The last SUD of a run — the table's "end SUD" column, with the same empty-run guard. */
export const endSud = (run: Exposition | null | undefined): number | null => {
  const points = (run?.suds ?? []).filter(
    (p): p is { sud: number; reactedAt: unknown } =>
      !!p && typeof p.sud === 'number',
  );
  return points.length ? points[points.length - 1].sud : null;
};

/** Drop rows the practice query returned once per permission edge (the L12 finding). */
export function dedupeById<T extends { _id?: string | null }>(
  rows: readonly T[] | null | undefined,
): T[] {
  const seen = new Set<string>();
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const id = row?._id;
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

// ── the expectancy answers (D-expectancies-2026-10-07) ───────────────────────────────────────────

/**
 * Did this exposition record ANY of the five optional answers? `false` reads as "not asked" — an
 * exposition recorded before the questions were added (the history shows one line for it instead of
 * blanks). Missing is never zero: a recorded 0 % is an answer.
 */
export const hasExpectancyAnswers = (
  run: Exposition | null | undefined,
): boolean =>
  !!run &&
  (!!run.expectedOutcome ||
    typeof run.expectedLikelihood === 'number' ||
    !!run.outcomeOccurred ||
    typeof run.likelihoodAfter === 'number' ||
    !!run.learned);

/**
 * The history's "Expected" cell: the feared outcome with the likelihood before → after, each part
 * only when it was answered — `My voice will shake … (80 % → 30 %)`, `(80 %)`, `(→ 30 %)`.
 */
export const expectedCellText = (
  run: Exposition | null | undefined,
): string => {
  const before =
    typeof run?.expectedLikelihood === 'number'
      ? `${run.expectedLikelihood} %`
      : '';
  const after =
    typeof run?.likelihoodAfter === 'number' ? `${run.likelihoodAfter} %` : '';
  const range =
    before && after
      ? `${before} → ${after}`
      : before || (after ? `→ ${after}` : '');
  return [run?.expectedOutcome ?? '', range ? `(${range})` : '']
    .filter(Boolean)
    .join(' ');
};
